import React, { useState } from 'react';
import './AutomationWizard.css';
import './ReportsAutomationWizard.css';
import ReportStepQuery from './steps/ReportStepQuery';
import ReportStepOutput, { isValidReportEmail } from './steps/ReportStepOutput';
import ReportStepSchedule from './steps/ReportStepSchedule';
import ReportStepReview from './steps/ReportStepReview';
import { apiService } from '../../../services/api';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

const REPORT_STEPS = [
  { number: 1, label: 'Query' },
  { number: 2, label: 'Output' },
  { number: 3, label: 'Schedule' },
  { number: 4, label: 'Review' },
];

const initialFormData = () => ({
  type: 'reports',
  automationName: '',
  reportQuery: '',
  queryName: '',
  selectedSavedQuery: '',
  reportEmailToSelf: true,
  reportRecipients: [],
  trigger: {
    scheduleType: 'scheduled',
    frequency: 'daily',
    time: '08:00',
    dayOfWeek: 'monday',
  },
  connectionId: null,
  datasetId: null,
  datasetName: '',
  datasetAction: 'update',
  timezone: defaultScheduleZone(),
});

const ReportsAutomationWizard = ({ isOpen, onClose, onShowNotification, initialQuery = '', initialStep = 1 }) => {
  const [currentStep, setCurrentStep] = useState(() => Math.min(4, Math.max(1, Number(initialStep) || 1)));
  const [formData, setFormData] = useState(initialFormData);
  const hasAppliedInitialQuery = React.useRef(false);

  // Prefill query when opened from chat "Schedule as report"; start at initialStep (e.g. 2 = Output)
  React.useEffect(() => {
    if (!isOpen) {
      hasAppliedInitialQuery.current = false;
      return;
    }
    if (initialQuery && typeof initialQuery === 'string') {
      const q = initialQuery.trim();
      if (q && !hasAppliedInitialQuery.current) {
        hasAppliedInitialQuery.current = true;
        setFormData((prev) => ({
          ...prev,
          reportQuery: q,
          queryName: q,
        }));
        const step = Math.min(4, Math.max(1, Number(initialStep) || 1));
        setCurrentStep(step);
      }
    }
  }, [isOpen, initialQuery, initialStep]);

  // Load current user email for "Email to self" (automation scheduler notification)
  React.useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    apiService
      .getCurrentUser()
      .then((user) => {
        if (cancelled || !user) return;
        const email = (user.email || (user.name && user.name.includes('@') ? user.name : null) || '').trim();
        if (email) {
          setFormData((prev) => ({ ...prev, userEmail: email }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleClose = () => {
    setCurrentStep(1);
    setFormData(initialFormData());
    onClose();
  };

  const handleCreate = async () => {
    try {
      const payload = {
        type: 'reports',
        datasetAction: 'update',
        trigger: {
          scheduleType: 'scheduled',
          frequency: formData.trigger?.frequency || 'daily',
          time: formData.trigger?.time || '08:00',
          dayOfWeek: formData.trigger?.dayOfWeek || 'monday',
        },
        automationName: formData.automationName || formData.reportQuery?.slice(0, 50) || 'Report',
        queryName: formData.queryName || formData.reportQuery || '',
        description: formData.description || null,
        timezone: formData.timezone || defaultScheduleZone(),
        reportEmailToSelf: formData.reportEmailToSelf !== false,
        reportRecipients: formData.reportRecipients ?? [],
        reportDatasetIds: Array.isArray(formData.reportDatasetIds) ? formData.reportDatasetIds : [],
        // Bind the report to the source saved query by id (PRD-1269).
        reportQueryThreadId: formData.selectedThreadId || null,
      };
      await apiService.createAutomation(payload);
      if (onShowNotification) {
        onShowNotification('Report automation created successfully!', 'success');
      }
      handleClose();
    } catch (error) {
      console.error('Error creating report automation:', error);
      if (onShowNotification) {
        onShowNotification(error.message || 'Failed to create report automation', 'error');
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <ReportStepQuery formData={formData} onSetFormData={setFormData} />;
      case 2:
        return <ReportStepOutput formData={formData} onSetFormData={setFormData} />;
      case 3:
        return <ReportStepSchedule formData={formData} onSetFormData={setFormData} />;
      case 4:
        return (
          <ReportStepReview
            formData={formData}
            onSetFormData={setFormData}
            onCreate={handleCreate}
            showCreateButton={false}
          />
        );
      default:
        return null;
    }
  };

  // Advance a step, but on the Output step first validate/commit any address the
  // user typed into "Email To Others" without clicking Add — so a bad address is
  // caught before proceeding, not silently ignored (PRD-1269).
  const handleNext = () => {
    if (currentStep === 2) {
      const pending = (formData.pendingRecipient || '').trim();
      if (pending) {
        if (!isValidReportEmail(pending)) {
          setFormData((f) => ({ ...f, recipientError: 'Enter a valid email address (e.g. name@example.com).' }));
          return;
        }
        setFormData((f) => {
          const recips = f.reportRecipients ?? [];
          return {
            ...f,
            reportRecipients: recips.includes(pending) ? recips : [...recips, pending],
            pendingRecipient: '',
            recipientError: '',
          };
        });
      }
    }
    setCurrentStep((s) => s + 1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!(formData.reportQuery || formData.queryName);
      case 2:
        return true;
      case 3:
        return true;
      case 4:
        return !!(formData.automationName && formData.automationName.trim() && (formData.queryName || formData.reportQuery || '').trim());
      default:
        return false;
    }
  };

  const isLastStep = currentStep === 4;

  if (!isOpen) return null;

  return (
    <div className="reports-automation-wizard-page">
      <div className="reports-wizard-container">
        <div className="reports-wizard-header">
          <div className="reports-wizard-breadcrumb">
            <span onClick={handleClose}>Automations</span>
            <span className="reports-wizard-breadcrumb-sep">/</span>
            <span className="reports-wizard-breadcrumb-current">New automation</span>
          </div>
          <div className="reports-wizard-header-row">
            <h2 className="reports-wizard-title">New automation</h2>
            <button type="button" className="reports-wizard-cancel" onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>

        <div className="reports-wizard-steps">
          {REPORT_STEPS.map((step) => (
            <div
              key={step.number}
              className={`reports-wizard-step ${currentStep === step.number ? 'reports-wizard-step-active' : ''} ${currentStep > step.number ? 'reports-wizard-step-done' : ''}`}
            >
              <span className="reports-wizard-step-num">{step.number}.</span>
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        <div className="reports-wizard-content">{renderStepContent()}</div>

        <div className="reports-wizard-footer">
          {currentStep > 1 && (
            <button
              type="button"
              className="reports-wizard-btn reports-wizard-btn-back"
              onClick={() => setCurrentStep((s) => s - 1)}
            >
              Back
            </button>
          )}
          {isLastStep ? (
            <button
              type="button"
              className="reports-wizard-btn reports-wizard-btn-primary"
              onClick={handleCreate}
              disabled={!canProceed()}
            >
              Create automation
            </button>
          ) : (
            <button
              type="button"
              className="reports-wizard-btn reports-wizard-btn-primary"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsAutomationWizard;
